import { CircleCheckFill } from '@gravity-ui/icons';
import classNames from 'classnames';
import type { TFuncKey } from 'i18next';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { layoutClassNames } from '@/utils/consts';

/**
 * The tenant's tagline. On split desktop the panel carries it, so the sign-in and register
 * headers drop their copy of it (see the split-screen block in index.css).
 */
export const brandTaglineKey = 'description.auth_subtitle';

const valueKeys = [
  'description.auth_value_campaigns',
  'description.auth_value_cashback',
  'description.auth_value_payouts',
] as const satisfies readonly TFuncKey[];

/**
 * Decorative brand side of the split desktop layout, built from tenant branding only. The logo
 * lives in the form column's header row, so the panel carries the tagline and value points.
 */
const BrandPanel = () => {
  const { t, i18n } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const [isIntroDone, setIsIntroDone] = useState(false);

  // `hidden lg:block` restarts CSS animations each time the viewport crosses 1024px, so the
  // intro plays once per load (or never, if the panel starts hidden) and is then switched off.
  useEffect(() => {
    const panel = panelRef.current;

    if (!panel || typeof panel.getAnimations !== 'function') {
      setIsIntroDone(true);
      return;
    }

    // eslint-disable-next-line @silverhand/fp/no-let -- cleared on unmount
    let isMounted = true;
    const finishAfterIntro = async () => {
      await Promise.allSettled(
        panel.getAnimations({ subtree: true }).map(async (animation) => animation.finished)
      );

      if (isMounted) {
        setIsIntroDone(true);
      }
    };

    void finishAfterIntro();

    return () => {
      // eslint-disable-next-line @silverhand/fp/no-mutation
      isMounted = false;
    };
  }, []);

  const introStyle = isIntroDone ? { animation: 'none' } : undefined;
  const tagline = t(brandTaglineKey);
  // A missing or blanked point (custom phrases) is dropped rather than shown as its raw key.
  const values = valueKeys
    .filter((key) => i18n.exists(key))
    .map((key) => t(key))
    .filter(Boolean);

  return (
    <div
      ref={panelRef}
      className={classNames(
        'brand-panel hidden h-full shrink-0 p-3 ps-0 lg:block',
        layoutClassNames.brandPanel
      )}
    >
      <div
        className="brand-panel__surface relative flex h-full flex-col justify-center overflow-hidden rounded-3xl p-12 xl:p-16"
        style={introStyle}
      >
        <div aria-hidden className="brand-panel__texture pointer-events-none absolute inset-0" />
        <div
          className="brand-panel__content relative flex max-w-md flex-col gap-8"
          style={introStyle}
        >
          {tagline && <p className="brand-panel__tagline">{tagline}</p>}
          {values.length > 0 && (
            <ul className="flex flex-col gap-4">
              {values.map((value) => (
                <li key={value} className="brand-panel__value flex items-start gap-3">
                  <CircleCheckFill aria-hidden className="mt-0.5 size-5 shrink-0" />
                  <span>{value}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrandPanel;
